import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  getBoostOrderDetail, 
  updateBoostOrderStatus, 
  expireBoost,
} from '../../services/api';
import { 
  FiArrowLeft,
  FiDollarSign,
  FiCalendar,
  FiUser,
  FiPackage,
  FiShoppingBag,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiAlertCircle,
  FiZap,
  FiCreditCard,
  FiRefreshCw,
  FiEdit2,
  FiTrash2,
  FiExternalLink,
  FiFileText,
  FiTrendingUp,
  FiInfo,
  FiShield,
  FiDatabase,
  FiCornerDownRight,
  FiMapPin,
  FiActivity
} from 'react-icons/fi';

function BoostOrderDetail({ admin }) {
  const { orderId } = useParams();
  const navigate = useNavigate();
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundData, setRefundData] = useState({
    refund_amount: 0,
    refund_reason: '',
    send_notification: true
  });

  useEffect(() => {
    fetchOrderDetail();
  }, [orderId]);

  const fetchOrderDetail = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getBoostOrderDetail(orderId);
      
      if (response.success) {
        setOrder(response.data);
        setRefundData(prev => ({
          ...prev,
          refund_amount: parseFloat(response.data.amount) || 0
        }));
        setSuccessMessage('Order details loaded successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.message || 'Failed to load order details');
      }
    } catch (err) {
      setError('Failed to load order details: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    if (!window.confirm(`Change order status to "${newStatus}"?`)) {
      return;
    }

    try {
      const response = await updateBoostOrderStatus(orderId, newStatus);
      if (response.success) {
        setSuccessMessage(`Order status updated to ${newStatus} successfully!`);
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchOrderDetail();
      } else {
        throw new Error(response.message || 'Update failed');
      }
    } catch (err) {
      setError('Failed to update order status: ' + err.message);
    }
  };

  const handleExpireBoost = async () => {
    if (!window.confirm('Are you sure you want to expire this boost? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await expireBoost(orderId);
      if (response.success) {
        setSuccessMessage('Boost expired successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchOrderDetail();
      } else {
        throw new Error(response.message || 'Expire failed');
      }
    } catch (err) {
      setError('Failed to expire boost: ' + err.message);
    }
  };

  

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours === 0) {
        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
      }
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    }
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-4 py-2 text-sm font-medium rounded-full flex items-center justify-center";
    
    const statusConfig = {
      'paid': { 
        bg: 'bg-green-900/30', 
        text: 'text-green-400 border-green-800/50', 
        label: 'Paid', 
        icon: <FiCheckCircle className="mr-2" /> 
      },
      'pending': { 
        bg: 'bg-yellow-900/30', 
        text: 'text-yellow-400 border-yellow-800/50', 
        label: 'Pending', 
        icon: <FiClock className="mr-2" /> 
      },
      'failed': { 
        bg: 'bg-red-900/30', 
        text: 'text-red-400 border-red-800/50', 
        label: 'Failed', 
        icon: <FiXCircle className="mr-2" /> 
      },
      'cancelled': { 
        bg: 'bg-gray-700', 
        text: 'text-gray-400 border-gray-600', 
        label: 'Cancelled', 
        icon: <FiXCircle className="mr-2" /> 
      },
      'refunded': { 
        bg: 'bg-blue-900/30', 
        text: 'text-blue-400 border-blue-800/50', 
        label: 'Refunded', 
        icon: <FiRefreshCw className="mr-2" /> 
      }
    };

    const config = statusConfig[status] || { 
      bg: 'bg-gray-700', 
      text: 'text-gray-400', 
      label: status, 
      icon: <FiAlertCircle className="mr-2" /> 
    };
    
    return (
      <span className={`${baseClasses} ${config.bg} ${config.text} border ${config.border || 'border-transparent'}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  const parseGatewayResponse = (gatewayResponse) => {
    if (!gatewayResponse) return null;
    try {
      return JSON.parse(gatewayResponse);
    } catch (err) {
      return null;
    }
  };

  // Loading Skeleton
  if (loading && !order) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="animate-pulse">
          {/* Header Skeleton */}
          <div className="mb-6">
            <div className="h-8 bg-gray-700 rounded w-1/3 mb-2"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2"></div>
          </div>

          {/* Main Content Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-gray-800 rounded-xl border border-gray-700 p-6">
                  <div className="h-6 bg-gray-700 rounded w-1/4 mb-4"></div>
                  <div className="space-y-3">
                    {[...Array(4)].map((_, j) => (
                      <div key={j} className="flex justify-between">
                        <div className="h-4 bg-gray-700 rounded w-1/3"></div>
                        <div className="h-4 bg-gray-700 rounded w-1/4"></div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-4">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="bg-gray-800 rounded-xl border border-gray-700 p-6">
                  <div className="h-6 bg-gray-700 rounded w-1/3 mb-4"></div>
                  <div className="space-y-2">
                    {[...Array(3)].map((_, j) => (
                      <div key={j} className="h-4 bg-gray-700 rounded w-full"></div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!order && !loading) {
    return (
      <div className="min-h-screen bg-gray-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-600 text-8xl mb-6">📦</div>
          <h3 className="text-2xl font-bold text-white mb-2">Order Not Found</h3>
          <p className="text-gray-400 mb-6">The boost order you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/admin/boost/orders')}
            className="px-6 py-3 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium flex items-center mx-auto hover:scale-[1.02]"
          >
            <FiArrowLeft className="mr-2" />
            Back to Boost Orders
          </button>
        </div>
      </div>
    );
  }

  const gatewayData = parseGatewayResponse(order.gateway_response);

  return (
    <div className="min-h-full bg-gray-900 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              <span className="text-[#ff660e]">Boost</span> Order Details
            </h1>
            <p className="text-gray-400 mt-1">Order #{order.order_id} • {formatRelativeTime(order.created_at)}</p>
          </div>
          <div className="flex gap-3">
            <Link
              to="/admin/boost/orders"
              className="px-4 py-2.5 bg-gray-800 text-white border border-gray-700 rounded-lg hover:bg-gray-700 hover:border-[#ff660e] transition-all duration-200 font-medium flex items-center hover:scale-[1.02]"
            >
              <FiArrowLeft className="mr-2" />
              Back to Orders
            </Link>
            <button
              onClick={fetchOrderDetail}
              className="px-4 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-all duration-200 font-medium flex items-center hover:scale-[1.02] shadow-lg"
            >
              <FiRefreshCw className="mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-900/20 border border-green-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiCheckCircle className="text-green-400 text-xl" />
            <p className="text-green-300 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-400 hover:text-green-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiAlertCircle className="text-red-400 text-xl" />
            <p className="text-red-300">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-400 hover:text-red-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Left Column - Order Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Summary Card */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-white flex items-center">
                <FiFileText className="mr-2 text-[#ff660e]" />
                Order Summary
              </h2>
              <div className="flex items-center space-x-2">
                {getStatusBadge(order.payment_status)}
                {order.active_boosts > 0 && (
                  <span className="px-3 py-1 bg-[#ff660e]/20 text-[#ff660e] text-sm font-medium rounded-full border border-[#ff660e]/30 flex items-center">
                    <FiZap className="mr-1" />
                    {order.active_boosts} Active Boost{order.active_boosts !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Order Information */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-400 mb-2">Order Information</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Order ID:</span>
                      <span className="font-medium text-white">#{order.order_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Reference:</span>
                      <span className="font-medium text-white font-mono text-sm">{order.payment_reference}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Created:</span>
                      <span className="font-medium text-white">{formatDate(order.created_at)}</span>
                    </div>
                    {order.paid_at && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Paid:</span>
                        <span className="font-medium text-green-400">{formatDate(order.paid_at)}</span>
                      </div>
                    )}
                    {order.updated_at && order.updated_at !== order.created_at && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Last Updated:</span>
                        <span className="font-medium text-gray-300">{formatDate(order.updated_at)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Boost Details */}
                <div>
                  <h3 className="text-sm font-medium text-gray-400 mb-2">Boost Details</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Duration:</span>
                      <span className="font-medium text-white">{order.duration_days} days</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Status:</span>
                      <span className={`font-medium ${order.active_boosts > 0 ? 'text-green-400' : 'text-gray-400'}`}>
                        {order.active_boosts > 0 ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {order.boost_start && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Started:</span>
                        <span className="font-medium text-white">{formatDate(order.boost_start)}</span>
                      </div>
                    )}
                    {order.boost_end && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Ends:</span>
                        <span className="font-medium text-red-400">{formatDate(order.boost_end)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Financial Information */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-400 mb-2">Financial Details</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Amount:</span>
                      <span className="text-2xl font-bold text-[#ff660e]">
                        {formatCurrency(order.amount)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Currency:</span>
                      <span className="font-medium text-white">{order.currency}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Payment Method:</span>
                      <div className="flex items-center">
                        <FiCreditCard className="mr-1 text-gray-400" />
                        <span className="font-medium text-white capitalize">
                          {order.payment_method?.replace(/_/g, ' ') || 'N/A'}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Gateway:</span>
                      <span className="font-medium text-white capitalize">
                        {order.payment_gateway?.replace(/_/g, ' ') || 'N/A'}
                      </span>
                    </div>
                    {order.transaction_id && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Transaction ID:</span>
                        <span className="font-medium text-white font-mono text-sm">{order.transaction_id}</span>
                      </div>
                    )}
                    {order.wallet_transaction_id && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Wallet TXN:</span>
                        <span className="font-medium text-blue-400 font-mono text-sm">{order.wallet_transaction_id}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* User & Listing Details Card */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-white mb-6 flex items-center">
              <FiUser className="mr-2 text-blue-400" />
              User & Listing Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* User Information */}
              <div className="space-y-4">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white text-lg font-bold">
                    {order.first_name?.charAt(0)}{order.last_name?.charAt(0)}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">
                      {order.first_name} {order.last_name}
                    </h3>
                    <p className="text-gray-400">{order.user_email}</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400">User ID:</span>
                    <span className="font-medium text-white">#{order.user_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Account Status:</span>
                    <span className="px-2 py-1 bg-green-900/30 text-green-400 text-xs font-medium rounded">
                      Active
                    </span>
                  </div>
                  <button
                    onClick={() => navigate(`/admin/users/${order.user_id}`)}
                    className="w-full mt-3 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg transition-colors font-medium flex items-center justify-center"
                  >
                    <FiExternalLink className="mr-2" />
                    View User Profile
                  </button>
                </div>
              </div>

              {/* Listing Information */}
              <div className="space-y-4">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-[#ff660e] to-orange-500 flex items-center justify-center">
                    <FiShoppingBag className="text-white text-xl" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white truncate">
                      {order.listing_title}
                    </h3>
                    <p className="text-gray-400">Listing #{order.listing_id}</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Store:</span>
                    <span className="font-medium text-white">{order.seller_store}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Boost Type:</span>
                    <div className="flex items-center">
                      <FiZap className="mr-1 text-[#ff660e]" />
                      <span className="font-medium text-white">{order.package_name}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(`/admin/listings/${order.listing_id}`)}
                    className="w-full mt-3 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg transition-colors font-medium flex items-center justify-center"
                  >
                    <FiExternalLink className="mr-2" />
                    View Listing Details
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Gateway Response Card */}
          {gatewayData && (
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-white mb-6 flex items-center">
                <FiDatabase className="mr-2 text-purple-400" />
                Payment Gateway Response
              </h2>
              
              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-sm text-gray-300 whitespace-pre-wrap">
                  {JSON.stringify(gatewayData, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Actions & Timeline */}
        <div className="space-y-6">
          {/* Actions Card */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-white mb-6 flex items-center">
              <FiActivity className="mr-2 text-[#ff660e]" />
              Order Actions
            </h2>
            
            <div className="space-y-3">
              {/* Status Update */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Update Status
                </label>
                <select
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent text-white transition-all duration-200"
                  value={order.payment_status}
                  onChange={(e) => handleStatusUpdate(e.target.value)}
                >
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="failed">Failed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="refunded">Refunded</option>
                </select>
              </div>

              {/* Boost Actions */}
              {order.payment_status === 'paid' && order.active_boosts > 0 && (
                <button
                  onClick={handleExpireBoost}
                  className="w-full px-4 py-2.5 bg-red-900/30 hover:bg-red-900/50 text-red-400 hover:text-red-300 rounded-lg font-medium flex items-center justify-center transition-all duration-200 hover:scale-[1.02] border border-red-800/50"
                >
                  <FiXCircle className="mr-2" />
                  Expire Boost
                </button>
              )}

              {/* Refund Action */}
              {order.payment_status === 'paid' && (
                <button
                  onClick={() => setShowRefundModal(true)}
                  className="w-full px-4 py-2.5 bg-blue-900/30 hover:bg-blue-900/50 text-blue-400 hover:text-blue-300 rounded-lg font-medium flex items-center justify-center transition-all duration-200 hover:scale-[1.02] border border-blue-800/50"
                >
                  <FiRefreshCw className="mr-2" />
                  Process Refund
                </button>
              )}

              {/* View Package */}
              <button
                onClick={() => navigate(`/admin/boost/packages/edit/${order.package_id}`)}
                className="w-full px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg font-medium flex items-center justify-center transition-all duration-200 hover:scale-[1.02]"
              >
                <FiPackage className="mr-2" />
                View Package
              </button>

              {/* Quick Actions */}
              <div className="pt-4 border-t border-gray-700">
                <h3 className="text-sm font-medium text-gray-300 mb-3">Quick Actions</h3>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => {
                      // Copy order reference
                      navigator.clipboard.writeText(order.payment_reference);
                      setSuccessMessage('Order reference copied!');
                      setTimeout(() => setSuccessMessage(''), 2000);
                    }}
                    className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded text-sm font-medium transition-colors"
                  >
                    Copy Ref
                  </button>
                  <button
                    onClick={() => {
                      // Export order details
                      const orderData = JSON.stringify(order, null, 2);
                      const blob = new Blob([orderData], { type: 'application/json' });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `boost-order-${order.order_id}.json`;
                      a.click();
                    }}
                    className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded text-sm font-medium transition-colors"
                  >
                    Export
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Timeline Card */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-white mb-6 flex items-center">
              <FiClock className="mr-2 text-yellow-400" />
              Order Timeline
            </h2>
            
            <div className="space-y-4">
              {/* Created */}
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                  <FiCheckCircle className="text-white text-xs" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">Order Created</p>
                  <p className="text-xs text-gray-400">{formatDate(order.created_at)}</p>
                  <p className="text-xs text-gray-500">Order was placed by user</p>
                </div>
              </div>

              {/* Paid */}
              {order.paid_at && (
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                    <FiDollarSign className="text-white text-xs" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">Payment Completed</p>
                    <p className="text-xs text-gray-400">{formatDate(order.paid_at)}</p>
                    <p className="text-xs text-gray-500">Payment was successfully processed</p>
                  </div>
                </div>
              )}

              {/* Updated */}
              {order.updated_at && order.updated_at !== order.created_at && (
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                    <FiEdit2 className="text-white text-xs" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">Order Updated</p>
                    <p className="text-xs text-gray-400">{formatDate(order.updated_at)}</p>
                    <p className="text-xs text-gray-500">Order details were modified</p>
                  </div>
                </div>
              )}

              {/* Boost Start */}
              {order.boost_start && (
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-[#ff660e] flex items-center justify-center flex-shrink-0">
                    <FiZap className="text-white text-xs" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">Boost Started</p>
                    <p className="text-xs text-gray-400">{formatDate(order.boost_start)}</p>
                    <p className="text-xs text-gray-500">Listing boost became active</p>
                  </div>
                </div>
              )}

              {/* Boost End (if applicable) */}
              {order.boost_end && (
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                    <FiXCircle className="text-white text-xs" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">Boost Ends</p>
                    <p className="text-xs text-gray-400">{formatDate(order.boost_end)}</p>
                    <p className="text-xs text-gray-500">Boost duration will expire</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Refund Modal */}
      {showRefundModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-xl border border-gray-700 shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Process Refund</h3>
                <button
                  onClick={() => setShowRefundModal(false)}
                  className="text-gray-400 hover:text-white text-xl"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Refund Amount (₦)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">₦</span>
                    <input
                      type="number"
                      className="w-full pl-10 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                      value={refundData.refund_amount}
                      onChange={(e) => setRefundData({
                        ...refundData,
                        refund_amount: parseFloat(e.target.value) || 0
                      })}
                      max={order.amount}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Maximum refundable: {formatCurrency(order.amount)}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Refund Reason
                  </label>
                  <textarea
                    className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-500"
                    rows="3"
                    value={refundData.refund_reason}
                    onChange={(e) => setRefundData({
                      ...refundData,
                      refund_reason: e.target.value
                    })}
                    placeholder="Explain the reason for this refund..."
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-blue-500 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                    checked={refundData.send_notification}
                    onChange={(e) => setRefundData({
                      ...refundData,
                      send_notification: e.target.checked
                    })}
                  />
                  <label className="ml-2 text-sm text-gray-300">
                    Send notification to user
                  </label>
                </div>

                <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600">
                  <h4 className="text-sm font-medium text-gray-300 mb-2">Refund Summary</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Original Amount:</span>
                      <span className="font-medium text-white">{formatCurrency(order.amount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Refund Amount:</span>
                      <span className="font-medium text-red-400">{formatCurrency(refundData.refund_amount)}</span>
                    </div>
                    <div className="pt-2 border-t border-gray-600">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-300">Remaining Balance:</span>
                        <span className="font-bold text-green-400">
                          {formatCurrency(order.amount - refundData.refund_amount)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowRefundModal(false)}
                className="px-4 py-2 text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 hover:text-white transition-colors font-medium hover:scale-[1.02]"
              >
                Cancel
              </button>
             
            </div>
          </div>
        </div>
      )}

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default BoostOrderDetail;